import { useState, Fragment } from "react";
import type { View } from "../../../../../common/types.ts";
import TopStuff from "./TopStuff.tsx";
import { StatsTable } from "./StatsTable.tsx";
import { RatingsRadar } from "./RatingsRadar.tsx";
import { CareerOverviewChart } from "./CareerOverviewChart.tsx";
import Injuries from "./Injuries.tsx";
import { helpers } from "../../util/helpers.ts";
import { groupAwards, translateAwardType } from "../../util/groupAwards.ts";
import { SafeHtml } from "../../components/SafeHtml.tsx";
import { translateEventText } from "../../util/eventText.ts";
import i18n from "../../util/i18n.ts";
import { formatPlayerNameFromLegacyName } from "../../util/playerNames.ts";
import PageTabs from "../../components/PageTabs.tsx";
import { MobileContentHero, MobileStatRow } from "../../mobile/mobilePrimitives.tsx";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";
import { resolvePlayerMobileContext, type PlayerMobileContext } from "../teamManagementDepth.ts";

const translateRelativeType = (type: string) =>
  i18n.t(`relativeType.${type}`, { ns: "ui", defaultValue: type });

type Props = Pick<
  View<"player">,
  | "bestPos"
  | "currentSeason"
  | "customMenu"
  | "events"
  | "feats"
  | "freeAgent"
  | "gender"
  | "injured"
  | "jerseyNumberInfos"
  | "leaders"
  | "phase"
  | "player"
  | "ratings"
  | "retired"
  | "showContract"
  | "showRatings"
  | "showTradeFor"
  | "spectator"
  | "statSummary"
  | "statTables"
  | "teamColors"
  | "teamJersey"
  | "teamName"
  | "teamURL"
  | "userTid"
  | "willingToSign"
>;

// ─── Tab IDs ──────────────────────────────────────────────────────────────────

const TABS = ["overview", "stats", "contract"] as const;
type TabId = (typeof TABS)[number];

const tabLabel = (id: TabId): string => {
  switch (id) {
    case "overview":
      return i18n.t("playerProfile.mobileTabOverview", {
        ns: "ui",
        defaultValue: "Overview",
      });
    case "stats":
      return i18n.t("playerProfile.mobileTabStats", {
        ns: "ui",
        defaultValue: "Stats",
      });
    case "contract":
      return i18n.t("playerProfile.mobileTabContract", {
        ns: "ui",
        defaultValue: "Contract",
      });
  }
};

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

// N1: the player return path is served by the global TitleBar back control
// (mobileBackStack). Related-player links still seed the stack via
// createMobileReturnUrl so back climbs player → player → source correctly; the
// current player's name is passed as the return label so the back control reads
// "‹player A›" when viewing a related player.
const createRelatedPlayerHref = ({
  activeTab,
  pid,
  returnLabel,
  sourceSection,
}: {
  activeTab: string;
  pid: number;
  returnLabel?: string;
  sourceSection: PlayerMobileContext["sourceSection"];
}) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab,
    originSection: sourceSection,
    pageID: "player",
    returnLabel,
    scrollY: getMobileScrollY(),
  });

// ─── Individual tab content components ────────────────────────────────────────

const RatingsTab = ({
  player,
  ratings,
  showRatings,
}: Pick<Props, "player" | "ratings" | "showRatings">) => {
  const latestRatings = player.ratings.at(-1);
  if (!latestRatings) {
    return (
      <p className="text-body-secondary">
        {i18n.t("common.none", { ns: "ui", defaultValue: "None" })}
      </p>
    );
  }

  const values: Record<string, number> = {};
  for (const key of ratings) {
    values[key] = (latestRatings as any)[key] ?? 0;
  }

  return (
    <>
      {showRatings ? (
        <div>
          <RatingsRadar
            ratings={ratings}
            values={values}
            ovr={latestRatings.ovr}
            pot={latestRatings.pot}
          />
        </div>
      ) : (
        <p className="text-body-secondary text-center my-4">
          {i18n.t("playerProfile.ratingsHidden", {
            ns: "ui",
            defaultValue: "Ratings are hidden.",
          })}
        </p>
      )}
    </>
  );
};

const StatsTab = ({
  player,
  statTables,
  leaders,
}: Pick<Props, "player" | "statTables" | "leaders">) => (
  <>
    {statTables.map(({ name, onlyShowIf, stats, superCols }) => (
      <StatsTable
        key={name}
        name={name}
        onlyShowIf={onlyShowIf}
        stats={stats}
        superCols={superCols}
        p={player}
        leaders={leaders}
      />
    ))}
  </>
);

const CareerTab = ({ player }: Pick<Props, "player">) => {
  const careerData = player.ratings.map((r) => ({
    season: r.season,
    ovr: r.ovr,
    pot: r.pot,
  }));

  return <CareerOverviewChart ratings={careerData} />;
};

const ContractTab = ({ player, showRatings }: Pick<Props, "player" | "showRatings">) => (
  <div>
    <h6 className="mt-2">
      {i18n.t("playerProfile.salariesSection", {
        ns: "ui",
        defaultValue: "Salaries",
      })}
    </h6>
    {player.salaries.length === 0 ? (
      <p>{i18n.t("common.none", { ns: "ui", defaultValue: "None" })}</p>
    ) : (
      <table className="table table-sm table-striped table-borderless">
        <thead>
          <tr>
            <th>{i18n.t("title.Year", { ns: "cols", defaultValue: "Year" })}</th>
            <th className="text-end">
              {i18n.t("title.Amount", {
                ns: "cols",
                defaultValue: "Amount",
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          {player.salaries.map((s, i) => (
            <tr
              key={i}
              className={
                s.type === "current" ? "fw-bold" : s.type === "future" ? "fst-italic" : undefined
              }
            >
              <td>{s.season}</td>
              <td className="text-end">{helpers.formatCurrency(s.amount, "M")}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>
              {i18n.t("playerProfile.totalSalary", {
                ns: "ui",
                defaultValue: "Total",
              })}
            </th>
            <th className="text-end">{helpers.formatCurrency(player.salariesTotal, "M")}</th>
          </tr>
        </tfoot>
      </table>
    )}

    <h6 className="mt-3">
      {i18n.t("playerProfile.injuriesSection", {
        ns: "ui",
        defaultValue: "Injuries",
      })}
    </h6>
    <Injuries injuries={player.injuries} showRatings={showRatings} />
  </div>
);

const HonorsTab = ({
  activeTab,
  player,
  feats,
  events,
  gender,
  playerMobileContext,
}: Pick<Props, "player" | "feats" | "events" | "gender"> & {
  activeTab: TabId;
  playerMobileContext: PlayerMobileContext;
}) => {
  const awardsGrouped = groupAwards(player.awards);

  return (
    <div>
      {/* Awards */}
      <h6 className="mt-2">
        {i18n.t("playerProfile.awardsSection", {
          ns: "ui",
          defaultValue: "Awards",
        })}
      </h6>
      {awardsGrouped.length > 0 ? (
        <div className="player-awards" data-v5-player-awards-list="">
          {awardsGrouped.map((a, i) => (
            <MobileStatRow
              key={i}
              label={
                a.count > 1
                  ? `${a.count}x ${translateAwardType(a.type)}`
                  : translateAwardType(a.type)
              }
              value={a.seasons.join(", ")}
            />
          ))}
        </div>
      ) : (
        <p>{i18n.t("common.none", { ns: "ui", defaultValue: "None" })}</p>
      )}

      {/* Relatives */}
      {player.relatives.length > 0 ? (
        <>
          <h6 className="mt-3">
            {i18n.t("playerProfile.relativesSection", {
              ns: "ui",
              defaultValue: "Relatives",
            })}
          </h6>
          <span
            data-player-relatives-owner-pid={player.pid}
            data-player-relatives-render-site="honors"
            data-player-relatives-state="complete"
            data-player-relatives-total-count={player.relatives.length}
            data-player-relatives-visible-count={player.relatives.length}
          >
            {player.relatives.map((rel) => (
              <Fragment key={rel.pid}>
                {translateRelativeType(helpers.getRelativeType(gender, rel.type))}:{" "}
                <a
                  data-player-relative-pid={rel.pid}
                  href={createRelatedPlayerHref({
                    activeTab,
                    pid: rel.pid,
                    returnLabel: formatPlayerNameFromLegacyName(player.name),
                    sourceSection: playerMobileContext.sourceSection,
                  })}
                >
                  {formatPlayerNameFromLegacyName(rel.name)}
                </a>
                <br />
              </Fragment>
            ))}
          </span>
        </>
      ) : null}

      {/* Notable Games */}
      {feats.length > 0 ? (
        <>
          <h6 className="mt-3">
            {i18n.t("playerProfile.statisticalFeats", {
              ns: "ui",
              defaultValue: "Notable Games",
            })}
          </h6>
          <div className="small-scrollbar" style={{ maxHeight: 300, overflowY: "auto" }}>
            {feats.map((e) => (
              <p key={e.eid} className="small">
                <b>{e.season}</b>: <SafeHtml dirty={translateEventText(e)} />
              </p>
            ))}
          </div>
        </>
      ) : null}

      {/* Transactions */}
      {events.length > 0 ? (
        <>
          <h6 className="mt-3">
            {i18n.t("playerProfile.transactionsSection", {
              ns: "ui",
              defaultValue: "Transactions",
            })}
          </h6>
          <div className="small-scrollbar" style={{ maxHeight: 300, overflowY: "auto" }}>
            {events.map((e) => (
              <p key={e.eid} className="small">
                <b>{e.season}</b>: <SafeHtml dirty={translateEventText(e)} />
              </p>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const PlayerMobile = (props: Props) => {
  const {
    bestPos,
    currentSeason,
    events,
    feats,
    freeAgent,
    gender,
    injured,
    jerseyNumberInfos,
    leaders,
    phase,
    player,
    ratings,
    retired,
    showContract,
    showRatings,
    showTradeFor,
    spectator,
    statSummary,
    statTables,
    teamColors,
    teamJersey,
    teamName,
    teamURL,
    userTid,
    willingToSign,
  } = props;

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const playerMobileContext = resolvePlayerMobileContext({
    playerTid: player.tid,
    search: typeof window !== "undefined" ? window.location.search : undefined,
  });
  const relatedPlayerReturnLabel = formatPlayerNameFromLegacyName(player.name);
  // v5Content: the management-page subtitle narration and the decision-hero
  // "roster decision" card (role/risk/value repeated the TopStuff bio, 法则二)
  // are gone. The first screen IS the player's core profile — the TopStuff
  // identity/ratings/awards card inside the content hero. N1: the source-return
  // back nav moved to the global TitleBar back control (mobileBackStack); PageTabs
  // stay as the accepted mobile pattern with content-first tab bodies.
  return (
    <div
      data-v5-player-profile=""
      data-mobile-player-action-mode={playerMobileContext.actionMode}
      data-mobile-player-display-name={relatedPlayerReturnLabel}
      data-mobile-player-legacy-name={player.name}
      data-mobile-player-source={playerMobileContext.sourceSection}
    >
      <PageTabs
        ariaLabel={i18n.t("menu.Players", {
          ns: "ui",
          defaultValue: "Players",
        })}
        className="player-mobile-page-tabs"
        tabs={TABS.map((id) => ({
          active: activeTab === id,
          key: id,
          label: tabLabel(id),
          onClick: () => {
            setActiveTab(id);
          },
        }))}
      />

      {/* Tab content */}
      <div className="py-2" data-mobile-player-overview-contract>
        {activeTab === "overview" && (
          <MobileContentHero data-v5-player-identity-first-screen="">
            <TopStuff
              bestPos={bestPos}
              currentSeason={currentSeason}
              freeAgent={freeAgent}
              gender={gender}
              getPlayerHref={(pid) =>
                createRelatedPlayerHref({
                  activeTab,
                  pid,
                  returnLabel: relatedPlayerReturnLabel,
                  sourceSection: playerMobileContext.sourceSection,
                })
              }
              injured={injured}
              jerseyNumberInfos={jerseyNumberInfos}
              phase={phase}
              player={player}
              retired={retired}
              showContract={showContract}
              showRatings={showRatings}
              showTradeFor={showTradeFor}
              spectator={spectator}
              statSummary={statSummary}
              teamColors={teamColors}
              teamJersey={teamJersey}
              teamName={teamName}
              teamURL={teamURL}
              userTid={userTid}
              willingToSign={willingToSign}
            />
            <RatingsTab player={player} ratings={ratings} showRatings={showRatings} />
          </MobileContentHero>
        )}
        {activeTab === "stats" && (
          <>
            <StatsTab player={player} statTables={statTables} leaders={leaders} />
            <details
              className="mt-3"
              data-mobile-player-career-depth=""
              data-native-details-host="player-career-depth"
            >
              <summary className="btn btn-outline-secondary btn-sm">
                {i18n.t("playerProfile.mobileCareerDepth", {
                  ns: "ui",
                  defaultValue: "Career and honors",
                })}
              </summary>
              <div className="mt-3">
                <CareerTab player={player} />
                <HonorsTab
                  activeTab={activeTab}
                  player={player}
                  playerMobileContext={playerMobileContext}
                  feats={feats}
                  events={events}
                  gender={gender}
                />
              </div>
            </details>
          </>
        )}
        {activeTab === "contract" && <ContractTab player={player} showRatings={showRatings} />}
      </div>
    </div>
  );
};

export default PlayerMobile;
